import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBar, BookOpen, Database, Sparkles, ArrowRight,
  Brain, Timer, MessageSquareQuote, ShieldCheck, Target, Zap,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';
import { goToGoogleAuth } from '@/lib/gmatApi';

const SECTIONS_INFO = [
  { key: 'quant', icon: ChartBar, title: 'Quantitative Reasoning', meta: '21 questions · 45 min',
    desc: 'Algebra, arithmetic, number properties, geometry, and ratios — sharpened with adaptive difficulty.' },
  { key: 'verbal', icon: BookOpen, title: 'Verbal Reasoning', meta: '23 questions · 45 min',
    desc: 'Critical reasoning, sentence correction, and reading comprehension at exam-level depth.' },
  { key: 'data_insights', icon: Database, title: 'Data Insights', meta: '20 questions · 45 min',
    desc: 'Data sufficiency, two-part analysis, graphics interpretation, and table analysis.' },
];

const FEATURES = [
  { icon: Brain, title: 'Adaptive Learning', desc: 'Difficulty adjusts after every answer — you only spend time on questions that move your score.' },
  { icon: Timer, title: 'Real Exam Simulation', desc: 'Three-section timed mock tests built to mirror the official GMAT structure end-to-end.' },
  { icon: MessageSquareQuote, title: 'AI-Powered Feedback', desc: 'Every question ships with an in-depth solution and a "watch out for" trap explanation.' },
];

const TRUST_POINTS = [
  { icon: ShieldCheck, label: '100% Original' },
  { icon: Target, label: '205–805 Estimate' },
  { icon: Zap, label: 'Instant Feedback' },
];

const GmatLandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="gmat-landing">
      <Seo
        title="GMAT Prep · Winhouse Tutors"
        description="AI-powered adaptive GMAT preparation with original Winhouse-authored questions, full-length mocks, and instant solutions."
        canonical="/gmat"
      />
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <FloatingActionButtons />

      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden bg-black text-white">
          {/* Layered gradient + soft glow */}
          <div
            className="absolute inset-0 opacity-90"
            style={{
              background:
                'radial-gradient(60% 50% at 20% 0%, rgba(0,122,122,0.45) 0%, rgba(0,122,122,0) 60%), radial-gradient(50% 40% at 85% 20%, rgba(211,216,111,0.18) 0%, rgba(211,216,111,0) 70%), linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
            }}
          />
          {/* Grid texture */}
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-24 sm:pt-36 sm:pb-32">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] mb-6">
                <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" /> Winhouse GMAT AI Learning
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                AI-Powered GMAT Prep
                <span className="block text-[#D3D86F]">by Winhouse Tutors</span>
              </h1>

              <p className="text-lg sm:text-xl text-white/75 max-w-2xl mt-6 leading-relaxed">
                An adaptive learning engine that re-paths your study after every answer.
                Original questions, instant solutions, and full-length mocks — built so you spend
                time only on what raises your score.
              </p>

              {/* High-conversion value-prop pill */}
              <div className="mt-6 inline-flex items-center gap-2 bg-[#D3D86F]/10 border border-[#D3D86F]/30 rounded-full pl-3 pr-4 py-2 text-sm sm:text-base">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D3D86F] text-black flex-shrink-0">
                  <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <span className="text-white font-semibold">Free GMAT Diagnostic</span>
                <span className="text-white/50">+</span>
                <span className="text-white font-semibold">Full Mock Tests</span>
                <span className="text-[#D3D86F] hidden sm:inline">Available</span>
              </div>

              <div className="flex flex-wrap gap-3 mt-9">
                <Button
                  onClick={() => goToGoogleAuth('/gmat/practice')}
                  className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 text-base rounded-full transition-transform hover:-translate-y-0.5"
                  data-testid="gmat-cta-start"
                >
                  Start Practice <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button
                  onClick={() => goToGoogleAuth('/gmat/mock-test')}
                  variant="outline"
                  className="bg-white/5 backdrop-blur border-white/25 text-white hover:bg-white/15 hover:text-white px-7 h-12 text-base rounded-full"
                  data-testid="gmat-cta-diagnostic"
                >
                  Take Mock Test
                </Button>
                <Link to="/get-started">
                  <Button
                    variant="ghost"
                    className="text-white/80 hover:text-white hover:bg-white/10 px-5 h-12 text-base rounded-full"
                    data-testid="gmat-cta-program"
                  >
                    Join GMAT Program →
                  </Button>
                </Link>
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-x-7 gap-y-3 mt-10 text-sm text-white/70">
                {TRUST_POINTS.map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2">
                    <Icon className="h-4 w-4 text-[#D3D86F]" /> {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Floating stats card */}
            <div className="hidden lg:block absolute right-10 top-32 w-[300px] rounded-2xl bg-white/[0.06] backdrop-blur-xl border border-white/15 p-5 shadow-2xl">
              <div className="text-xs uppercase tracking-widest text-white/60">Live score estimate</div>
              <div className="mt-2 flex items-end gap-2">
                <div className="text-5xl font-bold text-[#D3D86F]">685</div>
                <div className="text-xs text-white/60 mb-1.5">/ 805</div>
              </div>
              <div className="mt-4 space-y-2">
                {[['Quant', 78], ['Verbal', 71], ['Data Insights', 66]].map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-xs text-white/70 mb-1">
                      <span>{k}</span><span>{v}%</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-[#007A7A]" style={{ width: `${v}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-wider text-white/40">Sample dashboard</div>
            </div>
          </div>
        </section>

        {/* FEATURE GRID */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#007A7A] font-semibold mb-3">Why Winhouse GMAT</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Built for serious test-takers.</h2>
            <p className="text-gray-600 mt-3 text-base">Three things make the difference between a 600 and a 700+. We built the platform around all three.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group relative bg-white border border-gray-200 rounded-2xl p-7 hover:border-black hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mb-5 group-hover:bg-[#007A7A] transition-colors">
                  <Icon className="h-5 w-5 text-[#D3D86F]" />
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SECTIONS BREAKDOWN */}
        <section className="bg-[#FAF9F6] py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#007A7A] font-semibold mb-3">Three sections · One platform</div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Coverage that mirrors the real GMAT.</h2>
              </div>
              <Button
                onClick={() => goToGoogleAuth('/gmat/practice')}
                variant="outline"
                className="rounded-full border-black text-black hover:bg-black hover:text-white"
              >
                Start now <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {SECTIONS_INFO.map(({ key, icon: Icon, title, desc, meta }) => (
                <div
                  key={key}
                  className="bg-white rounded-2xl border border-gray-200 p-7 hover:border-[#007A7A] hover:-translate-y-0.5 transition-all"
                  data-testid={`gmat-section-${key}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#E6F4F4] flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-[#007A7A]" />
                  </div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-1.5">{meta}</div>
                  <h3 className="font-bold text-xl mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CALL TO ACTION */}
        <section className="bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Every question is an <span className="text-[#D3D86F]">original</span>.
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8 text-base sm:text-lg">
              We don't recycle official GMAT material — we craft questions that mirror the real test in
              difficulty, structure, and reasoning depth. No copyright issues. No shortcuts.
            </p>
            <Button
              onClick={() => goToGoogleAuth('/gmat/practice')}
              className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 rounded-full"
            >
              Begin Your Prep <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GmatLandingPage;
